import Script from 'next/script'
import { useMemo } from 'react'
import { IProgramData } from '../../../types'
import { IConfigFile } from '../../../utils'
import { ottRoot } from '../../Api'

export interface ICrumbShema {
  '@type'?: string
  position: number
  name?: string
  item?: string
}

export default function BreadcrumbsJsonLd({
  asPath,
  program,
  configFile,
}: {
  asPath: string
  program: IProgramData | null
  configFile: IConfigFile
}) {
  const baseUrl = 'https://13tv.co.il'

  const crumbList = useMemo<ICrumbShema[]>(() => {
    const path = asPath.split('/').filter(Boolean)
    const crumbs: ICrumbShema[] = [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'עמוד הבית',
        item: `${baseUrl}/`,
      },
    ]

    let accumulatedPath = ''
    let position = 2

    for (let index = 0; index < path.length; index++) {
      const segment = path[index]
      accumulatedPath += `/${segment}`

      let name = segment
      let item = `${baseUrl}${accumulatedPath}`

      //  allshows
      if (segment === 'allshows') {
        name = 'VOD'
        crumbs.push({
          '@type': 'ListItem',
          position,
          name,
          item,
        })
        position++
      }

      // /live
      else if (!isNaN(Number(segment)) && segment === '2010263') {
        name = configFile.live_h_title ?? 'שידור חי'
        item = `${baseUrl}${ottRoot}${accumulatedPath}`
        crumbs.push({
          '@type': 'ListItem',
          position,
          name,
          item,
        })
        position++
      }

      // /series
      else if (segment === 'series') {
        name = 'כל התוכניות'
        item = `${baseUrl}${ottRoot}${accumulatedPath}`
        crumbs.push({
          '@type': 'ListItem',
          position,
          name,
          item,
        })
        position++
      }

      // /series/ID
      else if (/^\d+$/.test(segment) && path[index - 1] === 'series') {
        name = program?.seriesName || `${segment}`
        item = `${baseUrl}${ottRoot}/series/${segment}`
        crumbs.push({
          '@type': 'ListItem',
          position,
          name,
          item,
        })
        position++
      }

      // /season/ID
      else if (/^\d+$/.test(segment) && path[index - 1] === 'season') {
        name = program?.season?.name || segment
        item = `${baseUrl}${ottRoot}/series/${program?.seriesId}/season/${segment}`
        crumbs.push({
          '@type': 'ListItem',
          position,
          name,
          item,
        })
        position++
      }

      // /episode
      else if (
        /^\d+$/.test(segment) &&
        path[index - 2] === 'season' &&
        path[index - 1] !== 'season'
      ) {
        name =
          program?.episodes?.filter(ep => ep.id === program?.episode)[0]
            ?.name || ''
        item = `${baseUrl}${ottRoot}/series/${program?.seriesId}/season/${program?.season.position}/${program?.episode}`
        crumbs.push({
          '@type': 'ListItem',
          position,
          name,
          item,
        })
        position++
      }
    }

    return crumbs
  }, [asPath, program, configFile])

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbList,
  }

  return (
    <Script
      id='BreadcrumbsJsonLd'
      type='application/ld+json'
      strategy='beforeInteractive'
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(breadcrumbJsonLd),
      }}
    />
  )
}
